import {
  Box, Card, CardContent, Chip, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import { useState, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import EventIcon from '@mui/icons-material/Event';
import VideocamIcon from '@mui/icons-material/Videocam';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import ChatIcon from '@mui/icons-material/Chat';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { EmptyState } from '@/components/EmptyState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { patient } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';
import type {
  AppointmentListItem,
  AppointmentStatus,
  Modality,
  PaymentStatus,
} from '@/types/medical';

/**
 * Patient appointments list.
 *
 * Tabs: Upcoming | Past | Cancelled. The first time the page loads, the
 * "upcoming" tab is selected if there's at least one upcoming appt;
 * otherwise we land on "past" so the user sees their history.
 */

const UPCOMING_STATUSES: AppointmentStatus[] = ['booked', 'confirmed', 'checked_in', 'in_consult'];
const PAST_STATUSES: AppointmentStatus[] = ['completed', 'no_show'];
const CANCELLED_STATUSES: AppointmentStatus[] = ['cancelled_pt', 'cancelled_dr', 'refunded'];

type TabKey = 'upcoming' | 'past' | 'cancelled';

export function PatientAppointmentsPage() {
  const q = useApiQuery({
    queryKey: ['patient', 'appointments', 'all'],
    queryFn:  patient.appointments.list,
  });

  const buckets = useMemo(() => {
    const all = q.data ?? [];
    const upcoming = all
      .filter((a) => UPCOMING_STATUSES.includes(a.status))
      .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
    const past = all
      .filter((a) => PAST_STATUSES.includes(a.status))
      .sort((a, b) => new Date(b.start_at).getTime() - new Date(a.start_at).getTime());
    const cancelled = all
      .filter((a) => CANCELLED_STATUSES.includes(a.status))
      .sort((a, b) => new Date(b.start_at).getTime() - new Date(a.start_at).getTime());
    return { upcoming, past, cancelled };
  }, [q.data]);

  const initialTab: TabKey =
    buckets.upcoming.length > 0 ? 'upcoming'
    : buckets.past.length > 0   ? 'past'
                                : 'upcoming';

  const [tab, setTab] = useState<TabKey>(initialTab);

  return (
    <>
      <PageHeader
        title="My appointments"
        subtitle="Past, upcoming, and cancelled visits."
      />

      <DataState query={q} loadingLabel="Loading appointments…">
        {() => (
          <>
            <Tabs
              value={tab}
              onChange={(_, v: TabKey) => setTab(v)}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                value="upcoming"
                label={`Upcoming (${buckets.upcoming.length})`}
              />
              <Tab
                value="past"
                label={`Past (${buckets.past.length})`}
              />
              <Tab
                value="cancelled"
                label={`Cancelled (${buckets.cancelled.length})`}
              />
            </Tabs>
            <ApptList items={buckets[tab]} bucket={tab} />
          </>
        )}
      </DataState>
    </>
  );
}

function ApptList({ items, bucket }: { items: AppointmentListItem[]; bucket: TabKey }) {
  if (items.length === 0) {
    return (
      <EmptyState
        title={
          bucket === 'upcoming' ? 'No upcoming appointments'
          : bucket === 'past'    ? 'No past appointments yet'
                                 : 'No cancelled appointments'
        }
        description={
          bucket === 'upcoming'
            ? "When you book a visit, it'll show up here."
            : undefined
        }
      />
    );
  }
  return (
    <Stack spacing={1.5}>
      {items.map((a) => <AppointmentCard key={a.id} appointment={a} />)}
    </Stack>
  );
}

function AppointmentCard({ appointment }: { appointment: AppointmentListItem }) {
  return (
    <Card
      component={RouterLink}
      to={`/patient/appointments/${appointment.id}`}
      sx={{
        textDecoration: 'none',
        color: 'inherit',
        transition: 'border-color 120ms',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <ModalityIcon modality={appointment.modality} />

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {appointment.procedure_name}
              </Typography>
              <StatusChip status={appointment.status} />
            </Stack>
            <Typography variant="body2" color="text.secondary" noWrap>
              {appointment.clinician_name} · {appointment.practice_name}
            </Typography>
          </Box>

          <Stack alignItems="flex-end" spacing={0.5} sx={{ minWidth: 180, textAlign: 'right' }}>
            <Typography variant="body2" className="tabular">
              {formatDateTime(appointment.start_at)}
            </Typography>
            <PaymentChip status={appointment.payment_status} />
          </Stack>

          <ChevronRightIcon color="action" />
        </Stack>
      </CardContent>
    </Card>
  );
}

function ModalityIcon({ modality }: { modality: Modality }) {
  const sx = {
    color: 'primary.main',
    bgcolor: 'background.subtle',
    borderRadius: 1.5,
    width: 40, height: 40,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  } as const;
  const icon = {
    in_person: <LocationOnIcon />,
    video:     <VideocamIcon />,
    phone:     <PhoneIcon />,
    async:     <ChatIcon />,
    home:      <HomeWorkIcon />,
    hybrid:    <EventIcon />,
  }[modality];
  return <Box sx={sx}>{icon}</Box>;
}

function StatusChip({ status }: { status: AppointmentStatus }) {
  const config: Record<AppointmentStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }> = {
    draft:        { label: 'draft', color: 'default' },
    booked:       { label: 'booked', color: 'primary' },
    confirmed:    { label: 'confirmed', color: 'primary' },
    checked_in:   { label: 'checked in', color: 'primary' },
    in_consult:   { label: 'in consult', color: 'primary' },
    completed:    { label: 'completed', color: 'success' },
    no_show:      { label: 'no-show', color: 'warning' },
    cancelled_pt: { label: 'cancelled', color: 'default' },
    cancelled_dr: { label: 'cancelled by clinician', color: 'warning' },
    refunded:     { label: 'refunded', color: 'default' },
  };
  const c = config[status];
  return <Chip size="small" label={c.label} color={c.color} variant="outlined" />;
}

function PaymentChip({ status }: { status: PaymentStatus }) {
  if (status === 'paid') {
    return <Chip size="small" label="paid" color="success" variant="outlined" sx={{ height: 22 }} />;
  }
  if (status === 'unpaid' || status === 'partial') {
    return <Chip size="small" label={status} color="warning" variant="outlined" sx={{ height: 22 }} />;
  }
  if (status === 'refunded') {
    return <Chip size="small" label="refunded" variant="outlined" sx={{ height: 22 }} />;
  }
  return null;
}
